// ── Route-aware navigation config for admin NavShell ──
// Maps nav labels to actual application routes.

export interface NavRouteItem {
  label: string;
  icon?: string;
  path?: string; // actual route path
  href?: string; // external URL — opens in a new tab
  children?: NavRouteItem[];
}

export interface NavRouteGroup {
  groupLabel: string;
  items: NavRouteItem[];
}

export interface SectionRouteConfig {
  items: NavRouteItem[];
  groups?: NavRouteGroup[];
}

export interface AdminNavConfig {
  rail: string[];
  sections: Record<string, SectionRouteConfig>;
}

// Shared student management items
const studentItems: NavRouteItem[] = [
  {
    label: "My Students",
    icon: "users",
    path: "/assessment/students",
    children: [
      { label: "Archived Students", icon: "archive", path: "/assessment/students/archived" },
    ],
  },
  { label: "Student Growth Areas", icon: "trendingUp", path: "/assessment/early-warning" },
];

// Associate (admin) — no archived students access
const associateStudentItems: NavRouteItem[] = [
  { label: "My Students", icon: "users", path: "/assessment/students" },
  { label: "Student Growth Areas", icon: "trendingUp", path: "/assessment/early-warning" },
];

export const ADMIN_NAV: AdminNavConfig = {
  rail: ["network", "learning", "assessments", "parents_students", "comms", "system"],
  sections: {
    network: {
      items: [
        { label: "National Overview", icon: "globe", path: "/national-dashboard" },
      ],
    },
    learning: {
      items: [
        { label: "Learning Dashboard", icon: "barChart", path: "/learning/dashboard" },
        {
          label: "Course Catalogue",
          icon: "bookOpen",
          path: "/learning/courses",
          children: [
            { label: "Microlearning", icon: "zap", path: "/learning/microlearning" },
            { label: "Competency Mapping", icon: "target", path: "/learning/competency" },
          ],
        },
        { label: "Learning Paths", icon: "list", path: "/learning/paths" },
        { label: "My Progress", icon: "target", path: "/learning/my-progress" },
        { label: "Learning Analytics", icon: "barChart", path: "/learning/learning-analytics" },
      ],
    },
    assessments: {
      items: [
        { label: "Quiz Dashboard", icon: "grid", path: "/assessment/dashboard" },
        {
          label: "Diagnostic Tests",
          icon: "stethoscope",
          path: "/assessment/diagnostic-tests",
          children: [
            { label: "Diagnostic Test Results", icon: "barChart", path: "/assessment/results" },
          ],
        },
        {
          label: "Homework",
          icon: "clapperboard",
          path: "/assessment/homework-videos",
          children: [
            { label: "Homework Handouts", icon: "fileText", path: "/assessment/homework-handouts" },
          ],
        },
        {
          label: "End of Term",
          icon: "calendar",
          path: "/assessment/end-of-term",
          children: [
            { label: "End of Term Results", icon: "barChart", path: "/assessment/end-of-term-results" },
            { label: "Manual Scoring", icon: "edit", path: "/assessment/scoring?scope=eot" },
          ],
        },
        {
          label: "Exams Management",
          icon: "clipboard",
          path: "/assessment/exams",
          children: [
            { label: "Exam Results", icon: "barChart", path: "/assessment/exam-results" },
            { label: "Manual Scoring", icon: "edit", path: "/assessment/scoring?scope=exam" },
          ],
        },
        { label: "Question Bank", icon: "database", path: "/assessment/question-bank" },
        
      ],
    },
    parents_students: {
      items: [
        ...studentItems,
      ],
    },
    comms: {
      items: [
        { label: "Communication", icon: "mail", path: "/communication" },
        { label: "Comms Center", icon: "inbox", path: "/comms-center" },
      ],
    },
    system: {
      items: [
        { label: "Tutor Audit", icon: "shield", path: "/learning/tutor-audit" },
        {
          label: "Admin Settings",
          icon: "settings",
          path: "/learning/admin-settings",
          children: [
            { label: "Users", icon: "users", path: "/users" },
            { label: "Centres", icon: "building", path: "/locations" },
            { label: "Audit Logs", icon: "list", path: "/learning/access-audit-logs" },
          ],
        },
        { label: "My Account", icon: "settings", path: "/profile" },
        { label: "Tutor Profiles", icon: "users", path: "/tutor-profiles" },
        {
          label: "Resources",
          icon: "bookOpen",
          children: [
            { label: "Understanding Lurneax", icon: "bookOpen", path: "/understanding-lurneax" },
            { label: "Running EOT Tests", icon: "calendar", path: "/running-eot-guide" },
            { label: "Running Exams", icon: "calendar", path: "/running-exam-guide" },
            { label: "Homework Videos & Handouts", icon: "bookOpen", path: "/homework-guide" },
            { label: "Schoolola Strategy", icon: "megaphone", path: "/learning/schoolola-strategy" },
          ],
        },
        { label: "Downloadable Resources", icon: "fileText", path: "/resources/downloads" },
        { label: "Project SID", icon: "projectSid", href: "https://projectsid.app/" },
      ],
    },
  },
};

// For regular admin — slightly reduced set (no operations, fewer admin items)
export const REGULAR_ADMIN_NAV: AdminNavConfig = {
  rail: ["network", "learning", "assessments", "parents_students", "system", "comms"],
  sections: {
    network: ADMIN_NAV.sections.network,
    learning: ADMIN_NAV.sections.learning,
    assessments: ADMIN_NAV.sections.assessments,
    parents_students: {
      items: [
        ...associateStudentItems,
      ],
    },
    system: {
      items: [
        {
          label: "Admin Settings",
          icon: "settings",
          path: "/learning/admin-settings",
          children: [
            { label: "Users", icon: "users", path: "/users" },
            { label: "Centres", icon: "building", path: "/locations" },
          ],
        },
        { label: "Project SID", icon: "projectSid", href: "https://projectsid.app/" },
      ],
    },
    comms: {
      items: [
        { label: "Communication", icon: "mail", path: "/communication" },
        { label: "Comms Center", icon: "inbox", path: "/comms-center" },
      ],
    },
  },
};

// Trimmed Learning section — hides Course Catalogue, Microlearning, Competency Mapping & Learning Paths for non-admin roles
const TRIMMED_LEARNING_SECTION: SectionRouteConfig = {
  items: [
    { label: "Learning Dashboard", icon: "barChart", path: "/learning/dashboard" },
    { label: "My Progress", icon: "target", path: "/learning/my-progress" },
    { label: "Learning Analytics", icon: "barChart", path: "/learning/learning-analytics" },
  ],
};

// Trimmed Assessments section — hides Question Bank for non-admin roles
const TRIMMED_ASSESSMENTS_SECTION: SectionRouteConfig = {
  items: ADMIN_NAV.sections.assessments.items.filter(
    (item) => item.label !== "Question Bank"
  ),
};

// State Partner — mirrors REGULAR_ADMIN_NAV but uses trimmed learning & assessments
export const STATE_PARTNER_NAV: AdminNavConfig = {
  ...REGULAR_ADMIN_NAV,
  sections: {
    ...REGULAR_ADMIN_NAV.sections,
    learning: TRIMMED_LEARNING_SECTION,
    assessments: TRIMMED_ASSESSMENTS_SECTION,
    parents_students: { items: [...studentItems] },
    system: {
      items: [
        ...REGULAR_ADMIN_NAV.sections.system.items,
        { label: "Downloadable Resources", icon: "fileText", path: "/resources/downloads" },
      ],
    },
  },
};


// Growth Partner — project-wide variant of State Partner (no state scoping).
// Same UI surfaces; data scoping is handled by RLS and per-component logic.
export const GROWTH_PARTNER_NAV: AdminNavConfig = {
  ...STATE_PARTNER_NAV,
};

// Centre Manager — no Network/National Overview, includes operations items
export const CENTRE_MANAGER_NAV: AdminNavConfig = {
  rail: ["learning", "assessments", "parents_students", "system", "comms"],
  sections: {
    learning: TRIMMED_LEARNING_SECTION,
    assessments: TRIMMED_ASSESSMENTS_SECTION,
    parents_students: {
      items: [
        ...studentItems,
        
      ],
    },
    system: {
      items: [
        { label: "Tutor Audit", icon: "shield", path: "/learning/tutor-audit" },
        { label: "Users", icon: "users", path: "/users" },
        { label: "My Account", icon: "settings", path: "/profile" },
        {
          label: "Resources",
          icon: "bookOpen",
          children: [
            { label: "Understanding Lurneax", icon: "bookOpen", path: "/understanding-lurneax" },
            { label: "Running EOT Tests", icon: "calendar", path: "/running-eot-guide" },
            { label: "Running Exams", icon: "calendar", path: "/running-exam-guide" },
            { label: "Homework Videos & Handouts", icon: "bookOpen", path: "/homework-guide" },
          ],
        },
        { label: "Downloadable Resources", icon: "fileText", path: "/resources/downloads" },
        { label: "Project SID", icon: "projectSid", href: "https://projectsid.app/" },
      ],
    },
    comms: {
      items: [
        { label: "Communication", icon: "mail", path: "/communication" },
      ],
    },
  },
};

// Tutor — trimmed: no Users/Centres/Communication
export const TUTOR_NAV: AdminNavConfig = {
  rail: ["learning", "assessments", "parents_students", "system"],
  sections: {
    learning: TRIMMED_LEARNING_SECTION,
    assessments: TRIMMED_ASSESSMENTS_SECTION,
    parents_students: {
      items: [
        ...studentItems,
      ],
    },
    system: {
      items: [
        { label: "My Account", icon: "settings", path: "/profile" },
        {
          label: "Resources",
          icon: "bookOpen",
          children: [
            { label: "Understanding Lurneax", icon: "bookOpen", path: "/understanding-lurneax" },
            { label: "Running EOT Tests", icon: "calendar", path: "/running-eot-guide" },
            { label: "Running Exams", icon: "calendar", path: "/running-exam-guide" },
            { label: "Homework Videos & Handouts", icon: "bookOpen", path: "/homework-guide" },
          ],
        },
        { label: "Downloadable Resources", icon: "fileText", path: "/resources/downloads" },
        { label: "Project SID", icon: "projectSid", href: "https://projectsid.app/" },
      ],
    },
  },
};

// Franchisee Prospect — read-only catalogue browsing only
export const FRANCHISEE_PROSPECT_NAV: AdminNavConfig = {
  rail: ["learning"],
  sections: {
    learning: {
      items: [
        { label: "Learning Dashboard", icon: "barChart", path: "/learning/dashboard" },
      ],
    },
  },
};

// Parent — assessment results, homework, exams, comms
export const PARENT_NAV: AdminNavConfig = {
  rail: ["assessments", "system"],
  sections: {
    assessments: {
      items: [
        { label: "Parent Dashboard", icon: "grid", path: "/parent-dashboard" },
        { label: "End of Term Results", icon: "barChart", path: "/assessment/end-of-term-results" },
        { label: "Exam Results", icon: "target", path: "/assessment/my-exam-results" },
        
        { label: "Resources", icon: "clapperboard", path: "/parent/homework" },
        { label: "Homework Handouts", icon: "fileText", path: "/parent/homework?view=handouts" },
        { label: "Downloadable Resources", icon: "fileText", path: "/resources/downloads" },
      ],
    },
    system: {
      items: [
        { label: "Comms Center", icon: "inbox", path: "/comms-center" },
        { label: "My Account", icon: "settings", path: "/parent/profile" },
      ],
    },
  },
};

// Student — minimal access to own results
export const STUDENT_NAV: AdminNavConfig = {
  rail: ["assessments", "system"],
  sections: {
    assessments: {
      items: [
        { label: "Diagnostic Test Results", icon: "barChart", path: "/assessment/results" },
      ],
    },
    system: {
      items: [
        { label: "Comms Center", icon: "inbox", path: "/comms-center" },
        { label: "My Account", icon: "settings", path: "/profile" },
      ],
    },
  },
};

